import { useEffect, useState } from "react";
import { ClioService } from "../../../api";
import { LitigationFunnelMatter } from "../../../api/models/LitigationFunnelMatter";
import { Areas, LitigationArea } from "./Areas";
import { Area } from "./Area";
import { Box, Button } from "@mui/material";
import { LitigationReportRequest } from "../../../api/models/LitigationReportRequest";
import { MatterData } from "../../../api/models/MatterData";
import { CustomFieldOption } from "../../../api/models/CustomField";
import FilterableSelect from "../../../components/basic/FilterableSelect";

type Props = {
    areas: LitigationArea[];
    matters: LitigationFunnelMatter[];
    matterOptions: MatterData[];
    loadReport: (filter: LitigationReportRequest) => void;
}

export default function StagesReport({
    areas,
    matters,
    matterOptions,
    loadReport
}: Props) {
    const [reportFilter, setReportFilter] = useState<LitigationReportRequest>({})
    
    const [assignedParalegals, setAssignedParalegals] = useState<CustomFieldOption[]>([]);
    const [responsibleAttorneys, setResponsibleAttorneys] = useState<CustomFieldOption[]>([]);
    const [seniorManagingAttorneys, setSeniorManagingAttornyes] = useState<CustomFieldOption[]>([]);
    const [supportingAttorneys, setSupportingAttorneys] = useState<CustomFieldOption[]>([]);

    useEffect(() => {
        loadCustomFieldOptions("Assigned Paralegal (AP)", setAssignedParalegals);
        loadCustomFieldOptions("Senior Managing Atty (SMA)", setSeniorManagingAttornyes);
        loadCustomFieldOptions("Supporting Atty (SA)", setSupportingAttorneys);
        loadCustomFieldOptions("Responsible Associate Atty (RAA)", setResponsibleAttorneys);
    }, [])

    const loadCustomFieldOptions = async (
        fieldName: string,
        setter: (options: CustomFieldOption[]) => void
        ): Promise<void> =>  {
        try {
            const customFields = await ClioService.getApiClioCustomFields(fieldName);
            const options = customFields.find(c => c.name === fieldName)?.picklist_options ?? [];
            setter(options.sort((a, b) => a.option.localeCompare(b.option)));
        } catch (error) {
            console.error(`Failed to load options for ${fieldName}`, error);
            setter([]);
        }
    }

    const handleLoadReport = () => {
        loadReport(reportFilter);
    }

    return (<>
        <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <FilterableSelect
                    label="Matter"
                    value={reportFilter.matterId}
                    options={matterOptions.map((matter) => ({
                        value: matter.id,
                        label: matter.display_number,
                    }))}
                    onChange={(matterId) => setReportFilter((prev) => ({
                        ...prev,
                        matterId: matterId as number | undefined,
                    }))}
                    minWidth={220}
                />

                <FilterableSelect
                    label="RAA"
                    value={reportFilter.raa}
                    includeNone
                    options={responsibleAttorneys.map((atty) => ({
                        value: atty.id,
                        label: atty.option,
                    }))}
                    onChange={(raa) => setReportFilter((prev) => ({
                        ...prev,
                        raa: raa as number | undefined,
                    }))}
                />

                <FilterableSelect
                    label="SMA"
                    value={reportFilter.sma}
                    includeNone
                    options={seniorManagingAttorneys.map((atty) => ({
                        value: atty.id,
                        label: atty.option,
                    }))}
                    onChange={(sma) => setReportFilter((prev) => ({
                        ...prev,
                        sma: sma as number | undefined,
                    }))}
                />

                <FilterableSelect
                    label="SA"
                    value={reportFilter.sa}
                    includeNone
                    options={supportingAttorneys.map((atty) => ({
                        value: atty.id,
                        label: atty.option,
                    }))}
                    onChange={(sa) => setReportFilter((prev) => ({
                        ...prev,
                        sa: sa as number | undefined,
                    }))}
                />

                <FilterableSelect
                    label="AP"
                    value={reportFilter.ap}
                    includeNone
                    options={assignedParalegals.map((atty) => ({
                        value: atty.id,
                        label: atty.option,
                    }))}
                    onChange={(ap) => setReportFilter((prev) => ({
                        ...prev,
                        ap: ap as number | undefined,
                    }))}
                />
                
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLoadReport}
                >
                    Load Report
                </Button>
            </Box>
            
            <Box 
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    gap: 4,
                    flexWrap: 'nowrap',
                    flex: 1,
                    padding: 1,
                }}>
                {areas.map(area => (
                    <Area
                        key={area.area}
                        area={area}
                        matters={matters.filter(m => m.area === area.area)}
                    />
                ))}
            </Box>
        </Box>
    </>);
}
